/**
 * Pusher Service for Vue.js Chat
 * Mirrors Flutter's PusherService implementation exactly
 *
 * Channels:
 * - private-conversation.{conversationId} - Conversation events
 * - private-user.{userId} - User notifications
 * - presence-chat - Online status
 *
 * Events handled:
 * - message.sent, message.edited, message.deleted, message.read
 * - reaction.added, reaction.removed
 * - user.typing, user.stopped_typing (client events)
 * - group.chat_toggled, group.user_kicked, group.user_added, group.user_left
 * - user.blocked, user.unblocked
 * - request.received, request.accepted, request.rejected
 * - group.created
 *
 * NOTE: Requires `npm install pusher-js` to enable real-time features.
 * Falls back gracefully to polling if Pusher is not available.
 */

import { apiUrl } from "@/utils/api";
import { chatState, ensureChatInitialized } from "@/stores/chat";

// Pusher will be loaded dynamically
let Pusher = null;

class PusherService {
  constructor() {
    this.pusher = null;
    this.initialized = false;
    this.subscribedChannels = new Map(); // channelName -> channel instance
    this.eventListeners = new Map(); // conversationId -> Set of callbacks
    this.userEventListeners = new Set(); // callbacks for user channel events
    this.typingTimers = new Map(); // conversationId -> timer for auto-stop typing
    // Connection state tracking (Flutter sync: _connectionState)
    this.connectionState = 'disconnected'; // 'connecting', 'connected', 'disconnected', 'reconnecting'
    this.connectionStateListeners = new Set(); // callbacks for connection state changes
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000; // Start with 1 second
    // Track reconnect timer to prevent duplicates and allow cleanup
    this.reconnectTimerId = null;
    // Track if we're in manual reconnect mode
    this.isManualReconnecting = false;
  }

  /**
   * Get current connection state
   */
  getConnectionState() {
    return this.connectionState;
  }

  /**
   * Subscribe to connection state changes
   */
  onConnectionStateChange(callback) {
    this.connectionStateListeners.add(callback);
    // Immediately call with current state
    callback(this.connectionState);
    return () => this.connectionStateListeners.delete(callback);
  }

  /**
   * Emit connection state change
   */
  _emitConnectionState(state) {
    // Prevent duplicate emissions
    if (this.connectionState === state) {
      return;
    }
    
    console.log(`[PusherService] Emitting state: ${this.connectionState} -> ${state}`);
    this.connectionState = state;
    this.connectionStateListeners.forEach(cb => {
      try {
        cb(state);
      } catch (e) {
        console.error('[PusherService] Connection state listener error:', e);
      }
    });
  }

  /**
   * Handle Pusher's internal state changes
   * Maps Pusher states to our simplified states:
   * - initialized, connecting -> 'connecting'
   * - connected -> 'connected'
   * - unavailable, failed, disconnected -> handled based on context
   */
  _handlePusherStateChange(current, previous) {
    // Clear any pending reconnect timer when state changes
    this._clearReconnectTimer();
    
    switch (current) {
      case 'connected':
        // Successfully connected - reset all reconnect state
        this.reconnectAttempts = 0;
        this.reconnectDelay = 1000;
        this.isManualReconnecting = false;
        this.initialized = true;
        this._emitConnectionState('connected');
        // Re-subscribe to channels after reconnect
        this._resubscribeChannels();
        break;
        
      case 'connecting':
        // Pusher is attempting to connect
        // Only show 'reconnecting' if we were previously connected
        if (previous === 'connected' || previous === 'unavailable') {
          this._emitConnectionState('reconnecting');
        } else {
          this._emitConnectionState('connecting');
        }
        break;
        
      case 'unavailable':
        // Connection lost, Pusher will auto-retry
        this._emitConnectionState('reconnecting');
        break;
        
      case 'failed':
        // Pusher gave up - we need to manually reconnect
        this._emitConnectionState('disconnected');
        this._scheduleReconnect();
        break;
        
      case 'disconnected':
        // Intentionally disconnected or initial state
        if (previous === 'connected' || previous === 'connecting') {
          this._emitConnectionState('disconnected');
        }
        break;
        
      default:
        console.log(`[PusherService] Unhandled state: ${current}`);
    }
  }

  /**
   * Clear any pending reconnect timer
   */
  _clearReconnectTimer() {
    if (this.reconnectTimerId) {
      clearTimeout(this.reconnectTimerId);
      this.reconnectTimerId = null;
    }
  }

  /**
   * Setup browser online/offline event listeners
   */
  _setupNetworkListeners() {
    // Store handlers for cleanup
    this._onlineHandler = () => {
      console.log("[PusherService] Browser came online");
      // Sync state when coming back online
      if (this.pusher) {
        const pusherState = this.pusher.connection.state;
        if (pusherState === 'connected') {
          this._emitConnectionState('connected');
        } else if (pusherState !== 'connecting') {
          // Trigger reconnect if not already connecting
          this.reconnect();
        }
      }
    };

    this._offlineHandler = () => {
      console.log("[PusherService] Browser went offline");
      this._emitConnectionState('disconnected');
    };

    window.addEventListener('online', this._onlineHandler);
    window.addEventListener('offline', this._offlineHandler);
  }

  /**
   * Remove network event listeners
   */
  _removeNetworkListeners() {
    if (this._onlineHandler) {
      window.removeEventListener('online', this._onlineHandler);
      this._onlineHandler = null;
    }
    if (this._offlineHandler) {
      window.removeEventListener('offline', this._offlineHandler);
      this._offlineHandler = null;
    }
  }

  /**
   * Initialize Pusher connection
   * Like Flutter's PusherService.initialize()
   */
  async initialize() {
    if (this.initialized && this.pusher) {
      return true;
    }

    // Try to load Pusher dynamically if not already loaded
    if (!Pusher) {
      try {
        // Dynamic import for pusher-js
        const pusherModule = await import("pusher-js").catch(() => null);
        if (pusherModule) {
          Pusher = pusherModule.default || pusherModule;
        }
      } catch (e) {
        console.warn(
          "[PusherService] pusher-js not available. Real-time features disabled."
        );
        console.warn(
          "[PusherService] To enable real-time: npm install pusher-js"
        );
        return false;
      }
    }

    if (!Pusher) {
      console.warn(
        "[PusherService] Pusher not available, falling back to polling"
      );
      return false;
    }

    await ensureChatInitialized();

    if (!chatState.userId) {
      console.warn("[PusherService] Cannot initialize - no userId");
      return false;
    }

    try {
      // Get Pusher config from environment or API
      const pusherKey =
        import.meta.env?.VITE_PUSHER_KEY ||
        process.env?.VUE_APP_PUSHER_KEY ||
        window.PUSHER_KEY;
      const pusherCluster =
        import.meta.env?.VITE_PUSHER_CLUSTER ||
        process.env?.VUE_APP_PUSHER_CLUSTER ||
        window.PUSHER_CLUSTER ||
        "ap2";
      const pusherAuthEndpoint =
        import.meta.env?.VITE_PUSHER_AUTH_ENDPOINT ||
        process.env?.VUE_APP_PUSHER_AUTH_ENDPOINT ||
        window.PUSHER_AUTH_ENDPOINT ||
        `${apiUrl}/pusher/auth`;

      if (!pusherKey) {
        console.warn(
          "[PusherService] No Pusher key configured. Set VITE_PUSHER_KEY or VUE_APP_PUSHER_KEY"
        );
        return false;
      }

      // Initialize Pusher with auth endpoint
      this.pusher = new Pusher(pusherKey, {
        cluster: pusherCluster,
        authEndpoint: pusherAuthEndpoint,
        auth: {
          headers: this._getAuthHeaders(),
        },
        enabledTransports: ["ws", "wss"],
        // Add authorizer to handle auth errors gracefully
        authorizer: (channel, options) => {
          return {
            authorize: (socketId, callback) => {
              fetch(pusherAuthEndpoint, {
                method: 'POST',
                headers: {
                  ...this._getAuthHeaders(),
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  socket_id: socketId,
                  channel_name: channel.name,
                }),
              })
                .then((response) => {
                  if (!response.ok) {
                    console.error('[PusherService] Auth failed:', response.status);
                    // Don't throw error, just log it
                    callback(null, { auth: '' });
                    return;
                  }
                  return response.json();
                })
                .then((data) => {
                  if (data) {
                    callback(null, data);
                  }
                })
                .catch((error) => {
                  console.error('[PusherService] Auth error:', error);
                  callback(null, { auth: '' });
                });
            },
          };
        },
      });

      // Connection state handlers (Flutter sync: _handleConnectionState)
      // Use Pusher's state_change event for reliable state tracking
      this.pusher.connection.bind("state_change", (states) => {
        console.log(`[PusherService] State change: ${states.previous} -> ${states.current}`);
        this._handlePusherStateChange(states.current, states.previous);
      });

      // Also bind to specific events for logging/debugging
      this.pusher.connection.bind("connected", () => {
        console.log("[PusherService] Connected event fired");
      });

      this.pusher.connection.bind("error", (err) => {
        console.error("[PusherService] Connection error:", err);
        // Don't change state here - let state_change handle it
      });

      // Emit initial state
      this._emitConnectionState('connecting');

      // Listen for browser online/offline events
      this._setupNetworkListeners();

      // Subscribe to user channel for notifications
      this._subscribeUserChannel();

      this.initialized = true;
      console.log("[PusherService] Initialized successfully");
      return true;
    } catch (e) {
      console.error("[PusherService] Initialization failed:", e);
      return false;
    }
  }

  /**
   * Get auth headers for Pusher
   */
  _getAuthHeaders() {
    const token = localStorage.getItem("access_token");
    const headers = {
      "Content-Type": "application/json",
    };
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
    return headers;
  }

  /**
   * Schedule a reconnection attempt with exponential backoff
   */
  _scheduleReconnect() {
    // Clear any existing timer first
    this._clearReconnectTimer();
    
    // Don't schedule if already connected or connecting
    if (this.connectionState === 'connected') {
      return;
    }
    
    // Check if Pusher is already trying to reconnect
    if (this.pusher && this.pusher.connection.state === 'connecting') {
      console.log("[PusherService] Pusher already reconnecting, skipping manual reconnect");
      return;
    }
    
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.log("[PusherService] Max reconnect attempts reached");
      this._emitConnectionState('disconnected');
      this.isManualReconnecting = false;
      return;
    }

    this.reconnectAttempts++;
    this.isManualReconnecting = true;
    const delay = Math.min(this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1), 30000);
    
    console.log(`[PusherService] Scheduling reconnect in ${delay}ms (attempt ${this.reconnectAttempts})`);
    
    this.reconnectTimerId = setTimeout(() => {
      // Double-check we're not already connected before attempting
      if (this.pusher && this.connectionState !== 'connected') {
        const pusherState = this.pusher.connection.state;
        if (pusherState !== 'connected' && pusherState !== 'connecting') {
          console.log("[PusherService] Attempting manual reconnect...");
          this.pusher.connect();
        } else {
          console.log(`[PusherService] Skipping reconnect, Pusher state is: ${pusherState}`);
        }
      }
      this.reconnectTimerId = null;
    }, delay);
  }

  /**
   * Re-subscribe to all channels after reconnect
   */
  _resubscribeChannels() {
    console.log("[PusherService] Re-subscribing to channels after reconnect");
    
    // Store channel names before clearing
    const channelNames = Array.from(this.subscribedChannels.keys());
    
    // Re-subscribe to each channel
    channelNames.forEach(channelName => {
      if (!this.pusher.channel(channelName)) {
        // Channel not subscribed, re-subscribe
        const channel = this.pusher.subscribe(channelName);
        this.subscribedChannels.set(channelName, channel);
        console.log("[PusherService] Re-subscribed to:", channelName);
      }
    });
  }

  /**
   * Force reconnect (user-initiated)
   */
  reconnect() {
    if (!this.pusher) {
      return this.initialize();
    }
    
    // Clear any pending reconnect timer
    this._clearReconnectTimer();
    
    // Reset reconnect state for fresh attempt
    this.reconnectAttempts = 0;
    this.reconnectDelay = 1000;
    this.isManualReconnecting = true;
    
    // Only emit reconnecting if not already connected
    const currentPusherState = this.pusher.connection.state;
    if (currentPusherState === 'connected') {
      console.log("[PusherService] Already connected, skipping reconnect");
      this._emitConnectionState('connected');
      return;
    }
    
    this._emitConnectionState('reconnecting');
    this.pusher.connect();
  }

  /**
   * Check if connected
   */
  isConnected() {
    return this.connectionState === 'connected';
  }
  
  /**
   * Sync our state with Pusher's actual state (for debugging/recovery)
   */
  syncConnectionState() {
    if (!this.pusher) {
      this._emitConnectionState('disconnected');
      return;
    }
    
    const pusherState = this.pusher.connection.state;
    console.log(`[PusherService] Syncing state, Pusher is: ${pusherState}`);
    
    if (pusherState === 'connected') {
      this._emitConnectionState('connected');
    } else if (pusherState === 'connecting') {
      this._emitConnectionState('reconnecting');
    } else {
      this._emitConnectionState('disconnected');
    }
  }

  /**
   * Subscribe to user's private channel
   * Like Flutter's subscription to private-user.{userId}
   */
  _subscribeUserChannel() {
    if (!this.pusher || !chatState.userId) return;

    const channelName = `private-user.${chatState.userId}`;

    if (this.subscribedChannels.has(channelName)) return;

    const channel = this.pusher.subscribe(channelName);
    this.subscribedChannels.set(channelName, channel);

    // Bind user-specific events
    channel.bind("request.received", (data) =>
      this._emitUserEvent("request.received", data)
    );
    channel.bind("request.accepted", (data) =>
      this._emitUserEvent("request.accepted", data)
    );
    channel.bind("request.rejected", (data) =>
      this._emitUserEvent("request.rejected", data)
    );
    channel.bind("group.created", (data) =>
      this._emitUserEvent("group.created", data)
    );

    console.log("[PusherService] Subscribed to user channel:", channelName);
  }

  /**
   * Subscribe to a conversation channel
   * Like Flutter's PusherService.subscribeConversation()
   */
  subscribeConversation(conversationId) {
    if (!this.pusher || !conversationId) return;

    const channelName = `private-conversation.${conversationId}`;

    if (this.subscribedChannels.has(channelName)) {
      console.log("[PusherService] Already subscribed to:", channelName);
      return;
    }

    const channel = this.pusher.subscribe(channelName);
    this.subscribedChannels.set(channelName, channel);

    // Bind all conversation events (matching Flutter's event handling)
    const events = [
      "message.sent",
      "message.edited",
      "message.deleted",
      "message.read",
      "reaction.added",
      "reaction.removed",
      "user.typing",
      "user.stopped_typing",
      "client-user.typing",
      "client-user.stopped_typing",
      "group.chat_toggled",
      "group.user_kicked",
      "group.user_added",
      "group.user_left",
      "user.blocked",
      "user.unblocked",
    ];

    events.forEach((eventName) => {
      channel.bind(eventName, (data) => {
        this._emitConversationEvent(conversationId, eventName, data);
      });
    });

    console.log("[PusherService] Subscribed to conversation:", channelName);
  }

  /**
   * Unsubscribe from a conversation channel
   * Like Flutter's PusherService.unsubscribeConversation()
   */
  unsubscribeConversation(conversationId) {
    if (!this.pusher || !conversationId) return;

    const channelName = `private-conversation.${conversationId}`;

    if (this.subscribedChannels.has(channelName)) {
      this.pusher.unsubscribe(channelName);
      this.subscribedChannels.delete(channelName);
      this.eventListeners.delete(conversationId);
      console.log("[PusherService] Unsubscribed from:", channelName);
    }
  }

  /**
   * Add event listener for a specific conversation
   * Like Flutter's PusherService.eventsForConversation()
   */
  onConversationEvent(conversationId, callback) {
    if (!this.eventListeners.has(conversationId)) {
      this.eventListeners.set(conversationId, new Set());
    }
    this.eventListeners.get(conversationId).add(callback);

    // Return unsubscribe function
    return () => {
      const listeners = this.eventListeners.get(conversationId);
      if (listeners) {
        listeners.delete(callback);
      }
    };
  }

  /**
   * Add event listener for user channel events
   */
  onUserEvent(callback) {
    this.userEventListeners.add(callback);
    return () => {
      this.userEventListeners.delete(callback);
    };
  }

  /**
   * Emit event to conversation listeners
   */
  _emitConversationEvent(conversationId, eventName, data) {
    const listeners = this.eventListeners.get(conversationId);
    if (listeners) {
      const event = { event: eventName, data };
      listeners.forEach((callback) => {
        try {
          callback(event);
        } catch (e) {
          console.error("[PusherService] Event handler error:", e);
        }
      });
    }
  }

  /**
   * Emit event to user channel listeners
   */
  _emitUserEvent(eventName, data) {
    const event = { event: eventName, data };
    this.userEventListeners.forEach((callback) => {
      try {
        callback(event);
      } catch (e) {
        console.error("[PusherService] User event handler error:", e);
      }
    });
  }

  /**
   * Trigger typing event (client event)
   * Like Flutter's PusherService.triggerTyping()
   */
  triggerTyping(conversationId, userName) {
    if (!this.pusher || !conversationId) return;

    const channelName = `private-conversation.${conversationId}`;
    const channel = this.subscribedChannels.get(channelName);

    if (channel) {
      channel.trigger("client-user.typing", {
        conversation_id: conversationId,
        user_id: chatState.userId,
        user_name: userName || chatState.userName,
      });
    }
  }

  /**
   * Trigger stopped typing event (client event)
   * Like Flutter's PusherService.triggerStoppedTyping()
   */
  triggerStoppedTyping(conversationId) {
    if (!this.pusher || !conversationId) return;

    const channelName = `private-conversation.${conversationId}`;
    const channel = this.subscribedChannels.get(channelName);

    if (channel) {
      channel.trigger("client-user.stopped_typing", {
        conversation_id: conversationId,
        user_id: chatState.userId,
      });
    }
  }

  /**
   * Disconnect and cleanup
   */
  disconnect() {
    // Clear reconnect timer
    this._clearReconnectTimer();
    this.isManualReconnecting = false;
    this.reconnectAttempts = 0;
    
    // Remove network listeners
    this._removeNetworkListeners();
    
    if (this.pusher) {
      // Unsubscribe from all channels
      this.subscribedChannels.forEach((channel, name) => {
        this.pusher.unsubscribe(name);
      });
      this.subscribedChannels.clear();
      this.eventListeners.clear();
      this.userEventListeners.clear();

      // Clear typing timers
      this.typingTimers.forEach((timer) => clearTimeout(timer));
      this.typingTimers.clear();

      this.pusher.disconnect();
      this.pusher = null;
      this.initialized = false;
      this._emitConnectionState('disconnected');
      console.log("[PusherService] Disconnected");
    }
  }

  /**
   * Check if Pusher is initialized
   */
  get isInitialized() {
    return this.initialized && this.pusher !== null;
  }
}

// Singleton instance (like Flutter's PusherService.instance)
export const pusherService = new PusherService();

export default pusherService;
